/**
 * Review presence client lifecycle (focus / keepalive / release).
 *
 * Phase 3 decision piggyback issues a new server token the client never receives.
 * After approve/reject, {@link notifyDecisionSuccess} clears the local token;
 * stale keepalive/release no-ops (accepted: false) are expected until the next focus claim.
 */

import type {
    ReviewPresenceFocusPutRequest,
    ReviewPresencePutResponse,
} from '@shared/review-presence-api';

/** First keepalive ~30s after focus (35s open expiry). */
export const REVIEW_PRESENCE_KEEPALIVE_FIRST_MS = 30_000;

/** Subsequent keepalive interval while the surface stays active. */
export const REVIEW_PRESENCE_KEEPALIVE_INTERVAL_MS = 30_000;

export type ReviewPresencePutFn = (
    body: ReviewPresenceFocusPutRequest | { intent: 'keepalive' | 'release'; cycleId: number; presenceToken: string },
) => Promise<ReviewPresencePutResponse | null>;

type LastFocus =
    | { kind: 'review_queue'; postingEventId: number }
    | { kind: 'evidence_drawer_store'; storeId: number }
    | { kind: 'evidence_drawer_task'; postingEventId: number };

export class ReviewPresenceLifecycle {
    private token: string | null = null;
    private focusRevision = 0;
    private focusQueue: Promise<void> = Promise.resolve();
    private surfaceActive = false;
    private tabVisible = true;
    private skipReleaseOnce = false;
    private lastFocus: LastFocus | null = null;
    private keepaliveFirstTimer: ReturnType<typeof setTimeout> | null = null;
    private keepaliveInterval: ReturnType<typeof setInterval> | null = null;

    constructor(
        private cycleId: number,
        private readonly putPresence: ReviewPresencePutFn,
        private enabled: boolean,
    ) {}

    setCycleId(cycleId: number): void {
        if (this.cycleId !== cycleId) {
            this.focusRevision++;
            this.token = null;
            this.lastFocus = null;
            this.stopKeepalive();
        }
        this.cycleId = cycleId;
    }

    setEnabled(enabled: boolean): void {
        this.enabled = enabled;
        if (!enabled) {
            this.focusRevision++;
            this.stopKeepalive();
            this.token = null;
            this.lastFocus = null;
        }
    }

    setTabVisible(visible: boolean): void {
        this.tabVisible = visible;
        if (!visible) {
            this.stopKeepalive();
            return;
        }
        if (this.surfaceActive && this.enabled) {
            void this.reclaimLastFocusIfNeeded();
        }
    }

    setSurfaceActive(active: boolean): void {
        const wasActive = this.surfaceActive;
        this.surfaceActive = active;

        if (!active && wasActive) {
            this.stopKeepalive();
            if (this.skipReleaseOnce) {
                this.skipReleaseOnce = false;
                this.token = null;
                return;
            }
            void this.releaseBestEffort();
            return;
        }

        if (active && !wasActive && this.tabVisible && this.enabled) {
            void this.reclaimLastFocusIfNeeded();
        }
    }

    /**
     * After a successful review decision.
     * - Clears client token (server may have issued a new one in Phase 3).
     * - When the surface will close (review queue modal), skips release so the 12s transition presence remains.
     */
    notifyDecisionSuccess(options?: { willCloseSurface?: boolean }): void {
        this.focusRevision++;
        this.token = null;
        this.stopKeepalive();
        if (options?.willCloseSurface) {
            this.skipReleaseOnce = true;
        }
    }

    async focusReviewQueue(postingEventId: number): Promise<void> {
        if (!this.canWrite()) return;
        this.lastFocus = { kind: 'review_queue', postingEventId };
        await this.sendFocus({
            intent: 'focus',
            cycleId: this.cycleId,
            surface: 'review_queue',
            postingEventId,
        });
    }

    async focusEvidenceDrawerStore(storeId: number): Promise<void> {
        if (!this.canWrite()) return;
        this.lastFocus = { kind: 'evidence_drawer_store', storeId };
        await this.sendFocus({
            intent: 'focus',
            cycleId: this.cycleId,
            surface: 'evidence_drawer',
            storeId,
        });
    }

    async focusEvidenceDrawerTask(postingEventId: number): Promise<void> {
        if (!this.canWrite()) return;
        this.lastFocus = { kind: 'evidence_drawer_task', postingEventId };
        await this.sendFocus({
            intent: 'focus',
            cycleId: this.cycleId,
            surface: 'evidence_drawer',
            postingEventId,
        });
    }

    dispose(): void {
        this.surfaceActive = false;
        this.stopKeepalive();
        void this.releaseBestEffort();
        this.enabled = false;
        this.token = null;
        this.lastFocus = null;
    }

    /** Exposed for tests. */
    getPresenceTokenForTests(): string | null {
        return this.token;
    }

    private canWrite(): boolean {
        return this.enabled && this.cycleId > 0;
    }

    private async sendFocus(body: ReviewPresenceFocusPutRequest): Promise<void> {
        const revision = ++this.focusRevision;
        this.skipReleaseOnce = false;
        // Preserve server write order as well as client token order during rapid navigation.
        const request = this.focusQueue.then(async () => {
            if (revision !== this.focusRevision || !this.canWrite()) return;
            const response = await this.putPresence(body);
            if (revision !== this.focusRevision) {
                if (response && 'presenceToken' in response && (!this.surfaceActive || !this.enabled || body.cycleId !== this.cycleId)) {
                    await this.putPresence({ intent: 'release', cycleId: body.cycleId, presenceToken: response.presenceToken });
                }
                return;
            }
            if (response && 'presenceToken' in response && response.accepted) {
                this.token = response.presenceToken;
                this.resetKeepaliveSchedule();
            }
        });
        this.focusQueue = request.catch(() => {});
        await request;
    }

    private async reclaimLastFocusIfNeeded(): Promise<void> {
        if (!this.surfaceActive || !this.tabVisible || !this.canWrite()) return;
        if (this.token) {
            this.resetKeepaliveSchedule();
            return;
        }
        if (!this.lastFocus) return;

        switch (this.lastFocus.kind) {
            case 'review_queue':
                await this.focusReviewQueue(this.lastFocus.postingEventId);
                break;
            case 'evidence_drawer_store':
                await this.focusEvidenceDrawerStore(this.lastFocus.storeId);
                break;
            case 'evidence_drawer_task':
                await this.focusEvidenceDrawerTask(this.lastFocus.postingEventId);
                break;
            default: {
                const _exhaustive: never = this.lastFocus;
                return _exhaustive;
            }
        }
    }

    private resetKeepaliveSchedule(): void {
        this.stopKeepalive();
        if (!this.surfaceActive || !this.tabVisible || !this.token) return;

        this.keepaliveFirstTimer = setTimeout(() => {
            void this.sendKeepalive();
            this.keepaliveInterval = setInterval(() => {
                void this.sendKeepalive();
            }, REVIEW_PRESENCE_KEEPALIVE_INTERVAL_MS);
        }, REVIEW_PRESENCE_KEEPALIVE_FIRST_MS);
    }

    private stopKeepalive(): void {
        if (this.keepaliveFirstTimer != null) {
            clearTimeout(this.keepaliveFirstTimer);
            this.keepaliveFirstTimer = null;
        }
        if (this.keepaliveInterval != null) {
            clearInterval(this.keepaliveInterval);
            this.keepaliveInterval = null;
        }
    }

    private async sendKeepalive(): Promise<void> {
        if (!this.token || !this.canWrite()) return;
        const token = this.token;
        const response = await this.putPresence({
            intent: 'keepalive',
            cycleId: this.cycleId,
            presenceToken: token,
        });
        if (!response || this.token !== token) return;
        if ('accepted' in response && response.accepted === false) {
            this.token = null;
            this.stopKeepalive();
        }
    }

    private async releaseBestEffort(): Promise<void> {
        this.focusRevision++;
        const token = this.token;
        this.token = null;
        if (!token || !this.canWrite()) return;
        await this.putPresence({
            intent: 'release',
            cycleId: this.cycleId,
            presenceToken: token,
        });
    }
}
